import { createAction } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { billing, UpgradeSource } from 'common/billing'
import { previewInvoiceForLiveChatSeats } from 'common/billing/actions/billingAuxiliaryActions'
import { alert } from 'common/core'
import { Ability } from 'common/core/constants/Ability'
import { getAbility } from 'common/core/selectors/abilitiesSelectors'
import { getUserId } from 'common/core/selectors/appSelectors'
import * as settingsActionTypes from 'common/settings/SettingsReduxActionTypes'
import { TEAM_MEMBER_LIMIT } from 'common/userRoles/constants/UserRoles'
import { getCurrentInboxUser } from 'common/userRoles/selectors/userRolesSelectors'
import { USER_THREADS_UNLIMITED, UserRoles } from 'common/userRoles/userRolesInterfaces'
import { UsersApi } from 'shared/api/requests/users'
import { createAsyncAction } from 'shared/lib/redux'
import { analyticsService } from 'utils/services/analytics'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

const NAMESPACE = 'userRoles'

export const updateRole = createAsyncAction<void, { role: UserRoles; userId: number }>(
  `${NAMESPACE}/updateRole`,
  async ({ role, userId }) => {
    await UsersApi.update({ body: { role_name: role, user_id: userId } })
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
    onBusinessError: (error) => {
      if (billing.isProError(error)) {
        billing.handleProError(error, {
          message: l.translate('Upgrade your plan to manage team members roles.'),
          showPaywall: error.error_data.go_pro,
          source: UpgradeSource.SETTINGS_TEAM,
          abilities: [Ability.AUTO_ASSIGN, Ability.USERS_TOTAL],
        })
        error.handle()
        return
      }

      if (UsersApi.setInboxUser.isBillingAccessError(error)) {
        alert(error.message, 'danger')
        error.handle()
      }
    },
  },
)

export const fetchUsers = createAsyncAction(
  `${NAMESPACE}/fetchUsers`,
  async () => {
    const { data } = await UsersApi.fetch()
    return data.users
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const removeAdmin = createAsyncAction(
  `${NAMESPACE}/removeAdmin`,
  async (userId: number, { dispatch }) => {
    await UsersApi.remove({ body: { user_id: userId } })
    await dispatch(fetchUsers())
  },
  {
    autoHandleByGuards: [UsersApi.remove.isFacebookAccessError],
    errorMarkupOptions: {
      scope: 'settings',
      section: null,
    },
  },
)

export const updateBillingAccess = createAsyncAction(
  `${NAMESPACE}/updateBillingAccess`,
  async ({ access, userId }: { access: boolean; userId: number }) => {
    await UsersApi.setBillingAccess({ body: { user_id: userId, billing_access: access } })
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const setAddPaidUserModalVisibility = createAction<boolean>(
  `${NAMESPACE}/setAddPaidUserModalVisibility`,
)

export const setDeletePaidUserModalVisibility = createAction<boolean>(
  `${NAMESPACE}/setDeletePaidUserModalVisibility`,
)

export const setBillingAccess = createAction<{ userId: number; access: boolean }>(
  `${NAMESPACE}/setBillingAccess`,
)

export const setUserRole = createAction<{ userId: number; role: UserRoles }>(
  `${NAMESPACE}/setUserRole`,
)

export const setCurrentInboxUser = createAction<{
  userId: number
  userName: string
}>(`${NAMESPACE}/setCurrentInboxUser`)

export const resetCurrentInboxUser = createAction(`${NAMESPACE}/resetCurrentInboxUser`)

export const applySetInboxUser = createAsyncAction(
  `${NAMESPACE}/applySetInboxUser`,
  async ({ isInboxUser, userId }: { isInboxUser: boolean; userId: number }) => {
    await UsersApi.setInboxUser({ body: { user_id: userId, is_inbox_user: isInboxUser } })
    return { applied: true }
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
    onBusinessError: (error) => {
      error.handle()

      if (billing.isProError(error)) {
        billing.handleProError(error, {
          showPaywall: error.error_data.go_pro,
          source: UpgradeSource.SETTINGS_TEAM,
          message: l.translate('Upgrade your plan to manage Inbox seats.'),
          abilities: [Ability.AUTO_ASSIGN, Ability.LIVECHAT_SEATS],
        })
        return
      }

      if (UsersApi.setInboxUser.isBillingAccessError(error)) {
        alert(error.message, 'danger')
      }
    },
  },
)

export const addInboxUser = createAsyncAction(
  `${NAMESPACE}/addInboxUser`,
  async (_, { dispatch, getState }) => {
    const { userId } = getCurrentInboxUser(getState())
    if (!userId) {
      alert('User id is not defined', 'danger')
      return
    }

    await dispatch(applySetInboxUser({ isInboxUser: true, userId })).unwrap()

    alert(l.translate('You added a new Inbox seat!'), 'success')
    dispatch(setAddPaidUserModalVisibility(false))
  },
)

export const deleteInboxUser = createAsyncAction(
  `${NAMESPACE}/deleteInboxUser`,
  async (_, { dispatch, getState }) => {
    const { userId } = getCurrentInboxUser(getState())
    if (!userId) {
      alert('User id is not defined', 'danger')
      return
    }

    await dispatch(applySetInboxUser({ isInboxUser: false, userId })).unwrap()

    dispatch(setDeletePaidUserModalVisibility(false))
    alert(
      l.translate('You deleted an Inbox seat. Your subscription price will be updated.'),
      'success',
    )
  },
)

export const setInboxUser = createAsyncAction(
  `${NAMESPACE}/setInboxUser`,
  async (
    { isInboxUser, userId }: { isInboxUser: boolean; userId: number },
    { dispatch, getState },
  ) => {
    const livechatSeats = getAbility(getState(), Ability.LIVECHAT_SEATS)
    const apply = () => dispatch(applySetInboxUser({ isInboxUser, userId })).unwrap()
    const alertMessage = () => {
      if (isInboxUser) {
        alert(l.translate('You added a new Inbox seat!'), 'success')
      } else {
        alert(l.translate('You deleted an Inbox seat.'), 'success')
      }
    }

    if (livechatSeats.isEnabled) {
      await apply()
      alertMessage()

      return { applied: true }
    }

    if (livechatSeats.isLimited && livechatSeats.can_extend_limit) {
      const preview = await dispatch(
        previewInvoiceForLiveChatSeats({
          seats_count: isInboxUser ? livechatSeats.used + 1 : livechatSeats.used - 1,
        }),
      ).unwrap()

      if (!preview.upgrade_tier) {
        await apply()
        alertMessage()

        return { applied: true }
      }

      const showPaidUserModalAction = isInboxUser
        ? setAddPaidUserModalVisibility(true)
        : setDeletePaidUserModalVisibility(true)
      dispatch(showPaidUserModalAction)

      return { applied: false }
    }

    await apply()

    return { applied: true }
  },
)

export const setConversationsLimits = createAsyncAction(
  `${NAMESPACE}/setConversationsLimits`,
  async ({
    userIds,
    limit,
  }: {
    limit: number | typeof USER_THREADS_UNLIMITED
    userIds: number[]
  }) => {
    await UsersApi.setConversationsLimit({
      body: { limit, user_ids: userIds },
    })

    alert(l.translate('Conversations assignment limit has been changed'), 'success')
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const setAutoAssignmentForUsers = createAsyncAction(
  `${NAMESPACE}/setAutoAssignmentForUsers`,
  async ({ userIds, isEnabled }: { userIds: number[]; isEnabled: boolean }) => {
    await UsersApi.setAutoAssignmentEnabled({
      body: { is_enabled: isEnabled, user_ids: userIds },
    })
  },
  {
    errorMarkupOptions: {
      scope: 'settings',
      section: 'team_members',
    },
  },
)

export const resetInviteLink = createAction(`${NAMESPACE}/resetInviteLink`)

export const generateInviteLink = createAsyncAction(
  `${NAMESPACE}/generateInviteLink`,
  async (role: UserRoles) => {
    const { data } = await UsersApi.generateInviteLink({ body: { role_name: role } })
    return data.link
  },
  {
    onBusinessError: (error) => {
      error.handle()

      if (billing.isProError(error)) {
        const message = l.translate(
          `You've reached your current team member limit ({TEAM_MEMBER_LIMIT}). Upgrade your plan to manage team members.`,
          { TEAM_MEMBER_LIMIT },
        )
        analyticsService.sendEvent('SETTINGS.INVITE_NEW_USER.GENERATE_LINK.ERROR')
        billing.handleProError(error, {
          showPaywall: error.error_data.go_pro,
          message,
          abilities: [Ability.USERS_TOTAL],
          source: UpgradeSource.MEMBER_LIMIT,
        })

        return
      }

      alert(error.message, 'danger')
    },
  },
)

anotherTabNotificationsListener.on(
  'account_user_billing_access_update',
  (data, dispatch, getState) => {
    const state = getState() as RootState
    const currentUserId = getUserId(state)
    const userId = data.model.target_user_id
    const billingAccess = data.model.value

    if (currentUserId === userId) {
      dispatch({
        type: settingsActionTypes.SETTINGS_UPDATE_CURRENT_USER_BILLING_ACCESS,
        hasBillingAccess: billingAccess,
      })
    }

    dispatch(setBillingAccess({ userId, access: billingAccess }))
  },
)

anotherTabNotificationsListener.on('account_user_role_update', (data, dispatch, getState) => {
  const state = getState() as RootState
  const currentUserId = getUserId(state)

  if (currentUserId === data.user_id) {
    dispatch({ type: settingsActionTypes.SETTINGS_UPDATE_CURRENT_USER_ROLE, role: data.new_role })
  }

  dispatch(setUserRole({ userId: data.user_id, role: data.new_role }))
})
